import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Trophy, Star, Gift, Loader2, Users,
  Heart, ShoppingBag, Lock, Flame, Crown,
  UtensilsCrossed, ArrowRight, Sparkles, CalendarDays, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const LEVEL_ICONS = {
  "utensils": UtensilsCrossed,
  "chef-hat": GustoIcon,
  "flame": Flame,
  "star": Star,
  "crown": Crown,
  "trophy": Trophy,
};

const LEVEL_COLORS = [
  "from-gray-400 to-gray-500",
  "from-green-400 to-emerald-500",
  "from-blue-400 to-indigo-500",
  "from-amber-400 to-orange-500",
  "from-purple-400 to-pink-500",
  "from-rose-500 to-red-600",
];

const ACTION_ICONS = {
  "users": Users,
  "chef-hat": GustoIcon,
  "calendar": CalendarDays,
  "star": Star,
  "receipt": ShoppingBag,
  "fridge": UtensilsCrossed,
  "wine": Gift,
};

const actionLabels = {
  meal_rating_received: { label: "Note reçue sur un plat", icon: Star },
  recipe_published: { label: "Recette publiée", icon: GustoIcon },
  referral: { label: "Parrainage d'un ami", icon: Users },
  recipe_reproduced: { label: "Recette reproduite", icon: Heart },
  first_recipe: { label: "Première recette", icon: Trophy },
  meal_organized: { label: "Repas organisé", icon: CalendarDays },
  receipt_scanned: { label: "Ticket de caisse scanné", icon: ShoppingBag },
  fridge_scanned: { label: "Scan du frigo", icon: UtensilsCrossed },
  wine_added: { label: "Bouteille ajoutée", icon: Gift },
  dish_scanned: { label: "Scan de plat", icon: Flame },
  redemption: { label: "Échange récompense", icon: Gift },
};

const PointsPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [pointsRes, configRes, rewardsRes] = await Promise.all([
          axios.get(`${API}/points/me`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/points/config`),
          axios.get(`${API}/rewards`),
        ]);
        setData(pointsRes.data);
        setConfig(configRes.data);
        setRewards(rewardsRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const redeemReward = async (rewardId) => {
    try {
      await axios.post(`${API}/rewards/redeem/${rewardId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Récompense échangée !");
      const res = await axios.get(`${API}/points/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalPoints = data?.total_points || 0;
  const level = data?.level || {};
  const currentLevel = level.current || { name: "Commis", level: 1 };
  const nextLevel = level.next;
  const progress = level.progress || 0;
  const pointsToNext = level.points_to_next || 0;
  const allLevels = data?.all_levels || [];
  const LevelIcon = LEVEL_ICONS[currentLevel.icon] || UtensilsCrossed;
  const levelColor = LEVEL_COLORS[currentLevel.level - 1] || LEVEL_COLORS[0];

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="points-page">
      <div className="max-w-2xl mx-auto space-y-5">

        {/* Level Card */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className={`bg-gradient-to-br ${levelColor} text-white overflow-hidden relative`}>
            <div className="absolute inset-0 opacity-10">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full bg-white"
                  style={{ left: `${15 + i * 15}%`, top: `${20 + (i % 3) * 25}%` }}
                  animate={{ y: [0, -10, 0], opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 2 + i * 0.3, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
            </div>
            <CardContent className="p-6 text-center relative">
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <LevelIcon className="w-14 h-14 mx-auto mb-2 drop-shadow-lg" />
              </motion.div>
              <p className="text-sm text-white/70 uppercase tracking-wide font-medium">Niveau {currentLevel.level}</p>
              <h2 className="font-heading text-3xl font-bold mt-1">{currentLevel.name}</h2>
              <p className="text-4xl font-black mt-2">{totalPoints} <span className="text-lg font-normal text-white/70">pts</span></p>

              {/* Progress to next level */}
              {nextLevel && (
                <div className="mt-4 space-y-2">
                  <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-white/80 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(progress, 100)}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                  </div>
                  <p className="text-sm text-white/80">
                    Encore <strong>{pointsToNext} pts</strong> pour devenir <strong>{nextLevel.name}</strong>
                  </p>
                </div>
              )}
              {!nextLevel && (
                <p className="text-sm text-white/80 mt-3">Niveau maximum atteint !</p>
              )}

              <div className="flex justify-center gap-6 mt-4">
                <div>
                  <p className="text-2xl font-bold">{data?.referrals_count || 0}</p>
                  <p className="text-xs text-white/60">Parrainages</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{data?.history?.length || 0}</p>
                  <p className="text-xs text-white/60">Actions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Levels Roadmap */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="w-4 h-4" /> Progression Chef
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="flex items-center gap-1 overflow-x-auto pb-2">
              {allLevels.map((lvl, i) => {
                const Icon = LEVEL_ICONS[lvl.icon] || Star;
                const reached = totalPoints >= lvl.min_points;
                const isCurrent = currentLevel.level === lvl.level;
                return (
                  <div key={lvl.level} className="flex items-center">
                    <motion.div
                      className={`flex flex-col items-center min-w-[64px] p-2 rounded-xl transition-all ${
                        isCurrent ? "bg-primary/10 ring-2 ring-primary" : reached ? "bg-green-50" : "opacity-40"
                      }`}
                      animate={isCurrent ? { scale: [1, 1.05, 1] } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Icon className={`w-5 h-5 ${reached ? "text-primary" : "text-gray-400"}`} />
                      <p className={`text-[10px] font-medium mt-1 text-center leading-tight ${reached ? "text-charcoal" : "text-gray-400"}`}>
                        {lvl.name}
                      </p>
                      <p className="text-[9px] text-muted-foreground">{lvl.min_points}pts</p>
                    </motion.div>
                    {i < allLevels.length - 1 && (
                      <div className={`w-4 h-0.5 ${reached ? "bg-primary" : "bg-gray-200"}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Next Reward */}
        {data?.next_reward && (
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-charcoal">{data.next_reward.name}</p>
                    {data.next_reward.partner_name && (
                      <p className="text-xs text-muted-foreground">chez {data.next_reward.partner_name}</p>
                    )}
                    <div className="mt-1.5">
                      <Progress value={(totalPoints / data.next_reward.points_cost) * 100} className="h-2" />
                    </div>
                    <p className="text-xs text-amber-700 mt-1 font-medium">
                      Il vous manque <strong>{data.next_reward.points_cost - totalPoints} pts</strong> — {data.next_reward.points_cost} pts requis
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Smart Suggestions */}
        {data?.suggestions?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> Comment gagner des points
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
              {data.suggestions.map((s, i) => {
                const Icon = ACTION_ICONS[s.icon] || Star;
                return (
                  <Link key={i} to={s.path}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group"
                    >
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-charcoal">{s.action}</p>
                      </div>
                      <Badge className="bg-green-100 text-green-700 font-bold">+{s.points} pts</Badge>
                      <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </motion.div>
                  </Link>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Points Config */}
        {config && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Barème des points</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 space-y-2">
              {Object.entries(config).map(([key, pts]) => {
                const info = actionLabels[key] || { label: key, icon: Star };
                const Icon = info.icon;
                return (
                  <div key={key} className="flex items-center gap-3 p-2 rounded-xl bg-gray-50">
                    <Icon className="w-4 h-4 text-primary" />
                    <span className="text-sm flex-1">{info.label}</span>
                    <Badge className="bg-amber-100 text-amber-700">+{pts} pts</Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Rewards Catalog */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Gift className="w-4 h-4" /> Récompenses
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {rewards.length > 0 ? (
              <div className="space-y-3">
                {rewards.map((r) => {
                  const canRedeem = totalPoints >= r.points_cost;
                  const pct = Math.min((totalPoints / r.points_cost) * 100, 100);
                  return (
                    <div key={r.id} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                        <Gift className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm text-charcoal">{r.name}</p>
                        {r.partner_name && (
                          <p className="text-xs text-muted-foreground">{r.partner_name}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={pct} className="h-1.5 flex-1" />
                          <span className="text-[10px] text-muted-foreground">{r.points_cost} pts</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        disabled={!canRedeem}
                        onClick={() => redeemReward(r.id)}
                        className="rounded-full"
                      >
                        {canRedeem ? "Échanger" : <Lock className="w-3 h-3" />}
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 space-y-3">
                <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground/30" />
                <div>
                  <p className="font-medium text-charcoal">Boutique Gusto</p>
                  <p className="text-sm text-muted-foreground">
                    Accessoires de cuisine, bons d'achats partenaires...
                  </p>
                  <Badge className="mt-2 bg-amber-100 text-amber-700">Prochainement disponible</Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* History */}
        {data?.history?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Historique récent</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-1">
                {data.history.slice(0, 10).map((h, i) => {
                  const info = actionLabels[h.action] || { label: h.action, icon: Star };
                  const Icon = info.icon;
                  const isNegative = h.points < 0;
                  return (
                    <motion.div
                      key={h.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.03 }}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50"
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isNegative ? "bg-red-100" : "bg-green-100"
                      }`}>
                        <Icon className={`w-3.5 h-3.5 ${isNegative ? "text-red-500" : "text-green-600"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{h.description}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(h.created_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                      <span className={`font-bold text-sm ${isNegative ? "text-red-500" : "text-green-600"}`}>
                        {isNegative ? "" : "+"}{h.points}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <div className="flex gap-3">
          <Link to="/friends" className="flex-1">
            <Button variant="outline" className="w-full rounded-full gap-2">
              <Users className="w-4 h-4" /> Parrainer un ami
            </Button>
          </Link>
          <Link to="/leaderboard" className="flex-1">
            <Button className="w-full rounded-full gap-2">
              <Trophy className="w-4 h-4" /> Classement
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PointsPage;
